import { useMemo, useState } from "react";
import { Modal, SegmentedControl, Stack, Text } from "@mantine/core";
import { AttachmentLanguage, AttachmentNumber, buildAttachmentHtml, CTC_ATTACHMENT_TITLES, CTC_LOGO_DOC, CTC_LOGO_FIELD, ctcAttachmentUsesCustomerLogo, ctcLogoDataUrl } from "../../../../shared/ctcAttachments";
import { CtcCustomer, CtcPurchaseOrder } from "../../types";
import { useLiveDoc } from "./useLive";

/** Sample order data so a template can be seen as the customer would receive it. */
const at = (iso: string) => ({ toDate: () => new Date(iso) });
const samplePo = (language: AttachmentLanguage) => ({
    referenceNumber: "SAMPLE-26-00001",
    customerId: "sample",
    amount: 150000,
    currencyId: "KWD",
    commissionId: "Standard",
    clientRefNo: "CL-1001",
    created: at("2026-10-14T08:30:00Z"),
    submittedAt: at("2026-10-14T08:35:00Z"),
    purchasedAt: at("2026-10-14T10:00:00Z"),
    transferredAt: at("2026-10-14T11:00:00Z"),
    soldAt: at("2026-10-14T12:00:00Z"),
    updated: at("2026-10-14T11:00:00Z"),
    quantity: 500,
    unitPrice: 300,
    locationName: "Bursa Malaysia",
    certificateNumber: "CERT-0001",
    customerCommission: 75,
    commissionCurrency: { customer: "KWD" },
    soldMethod: "whatsapp",
    makerEmail: "maker@kfh.com",
    assignedDealerEmail: "dealer@kfhbrokerage.com",
    beneficiary: { number: "295050706387", mobile: "51234567", mobile_code: "+965", language },
}) as unknown as CtcPurchaseOrder;

interface Props {
    opened: boolean;
    onClose: () => void;
    number: AttachmentNumber;
    /** Template text to show; omitted = built-in text. */
    text?: { en: string; ar: string };
    title?: string;
    customer?: Pick<CtcCustomer, "name_en">;
}

/** Preview of an attachment exactly as the PDF is built (shared/ctcAttachments.ts), with sample order data. */
export function CtcAttachmentPreview({ opened, onClose, number, text, title, customer }: Props) {
    const [language, setLanguage] = useState<AttachmentLanguage>("ar");
    // 2, 4, 5 show the CTC logo (ctc_config/ctcTemplates.ctc_logo); 1, 3 the customer's (not loaded here: its name is shown)
    const { data: logoDoc } = useLiveDoc<Record<string, unknown>>(opened && !ctcAttachmentUsesCustomerLogo(number) ? CTC_LOGO_DOC : null);
    const logoDataUrl = ctcAttachmentUsesCustomerLogo(number) ? undefined : ctcLogoDataUrl(logoDoc?.[CTC_LOGO_FIELD]);
    const html = useMemo(() => buildAttachmentHtml(number, {
        po: samplePo(language),
        customer: { name_en: customer?.name_en || "Sample customer" } as CtcCustomer,
        commodity: { name_en: "Palm crude oil", measurementUnit: "MT" } as never,
        broker: { name_en: "Sample broker" } as never,
        commission: { name_en: "Standard Trading Commission", name_ar: "عمولة التداول القياسية" },
        language,
        text,
        logoDataUrl,
    }), [number, text, language, customer?.name_en, logoDataUrl]);

    return (
        <Modal opened={opened} onClose={onClose} size="56rem" title={title ?? `Attachment ${number}: ${CTC_ATTACHMENT_TITLES[number]}`}>
            <Stack gap="xs">
                <SegmentedControl size="xs" w="fit-content" value={language} onChange={(v) => setLanguage(v as AttachmentLanguage)}
                    data={[{ value: "ar", label: "Arabic order" }, { value: "en", label: "English order" }]} />
                <Text size="xs" c="dimmed">Sample order data. The PDF uses the text in the order's language.</Text>
                {!ctcAttachmentUsesCustomerLogo(number) && !logoDataUrl && (
                    <Text size="xs" c="orange">No CTC logo set ({CTC_LOGO_DOC}.{CTC_LOGO_FIELD}): "Commodity Trading Center" is printed instead.</Text>
                )}
                {/* sandbox: the page can't run scripts or reach the portal */}
                <iframe title="Attachment preview" sandbox="" srcDoc={html} style={{ width: "100%", height: "70vh", border: "1px solid var(--mantine-color-gray-3)", borderRadius: 6, background: "white" }} />
            </Stack>
        </Modal>
    );
}
